import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Avatar, Button, Dropdown, Typography } from "antd";
import type { MenuProps } from "antd";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import deleteIcon from "../../../assets/images/delete-icon.svg";
import warningIcon from "../../../assets/images/warning.svg";
import StatusBadge from "../../../components/Badges/StatusBadge";
import { CustomerEnum } from "../../../enums/customer";
import { PermissionEnum } from "../../../enums/permission";
import { RouteNameEnum } from "../../../enums/routeName";
import { ServiceNameEnum } from "../../../enums/serviceName";
import usePermission from "../../../hooks/usePermission";
import useServices from "../../../hooks/useServices";
import { AxiosService } from "../../../utils/axiosService";
import { DateTimeUtils } from "../../../utils/dateTime";
import ConfirmModal from "./ConfirmModal";
import WarningDeleteCustomerModel from "./WarningDeleteCustomerModel";

interface DashedBorderCardProps {
  title: string;
  value: string;
}
const DashedBorderCard: React.FC<DashedBorderCardProps> = (
  props: DashedBorderCardProps,
) => {
  const { title, value } = props;
  return (
    <div
      style={{
        padding: "8px 16px",
        borderRadius: "8px",
        border: "1px dashed #9E9E9E",
      }}
    >
      <Typography.Text type="secondary">
        {title}
      </Typography.Text>
      <div>
        <Typography.Text type="secondary">
          {DateTimeUtils.dateFormatStartWithDay(value)}
        </Typography.Text>
      </div>
    </div>
  );
};

interface MoreOptionMenuProps {
  isActive: boolean;
  isSso: boolean;
  handleConfirmSendForgotPasswordLink: () => void;
  handleDeactivateAccountConfirm: () => void;
  handleDeleteConfirm: () => void;
  handleActivateAccountConfirm: () => void;
}

const MoreOptionMenu: React.FC<MoreOptionMenuProps> = (
  props: MoreOptionMenuProps,
) => {
  const {
    handleDeactivateAccountConfirm,
    handleDeleteConfirm,
    handleActivateAccountConfirm,
    isActive,
  } = props;
  const { checkPermission } = usePermission();

  const items: MenuProps["items"] = [];

  if (checkPermission([PermissionEnum.code.customerDeactivate])) {
    items.push(
      isActive
        ? {
          key: "deactivate",
          label: "Deactivate account",
          onClick: () => handleDeactivateAccountConfirm(),
        }
        : {
          key: "activate",
          label: "Active account",
          onClick: () => handleActivateAccountConfirm(),
        },
    );
  }
  if (checkPermission([PermissionEnum.code.customerDelete])) {
    items.push({
      key: "delete",
      label: "Delete account",
      onClick: () => handleDeleteConfirm(),
    });
  }

  return (
    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomLeft">
      <Button
        style={{
          width: 30,
          height: 30,
          border: "1px solid #9E9E9E",
          borderRadius: "6px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
      >
        <MoreHorizIcon />
      </Button>
    </Dropdown>
  );
};

export type CustomerDetailT = {
  imageURL: string;
  displayName: string;
  isActive: boolean;
  email: string;
  createdAt: string;
  latestLoginAt: string;
  isSso: boolean;
  updatedAt: string;
};
interface CustomerDetailCardProps {
  customerDetailCard: CustomerDetailT;
  refetchData: () => void;
}

const CustomerDetailCard: React.FC<CustomerDetailCardProps> = (
  props: CustomerDetailCardProps,
) => {
  const { customerDetailCard, refetchData } = props;
  const {
    imageURL,
    displayName,
    isActive,
    email,
    createdAt,
    latestLoginAt,
    updatedAt,
    isSso,
  } = customerDetailCard;

  const [successDialogState, setSuccessDialogState] = React.useState({
    isOpen: false,
    content: "",
  });
  const { id: customerId } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = usePermission();

  const [isOpenDeleteCustomerModal, setIsOpenDeleteCustomerModal] =
    React.useState<boolean>(false);
  const [isOpenDeactivateModal, setIsOpenDeactivateModal] =
    React.useState<boolean>(false);
  const [isOpenActivateModal, setIsOpenActivateModal] =
    React.useState<boolean>(false);
  const [isOpenWarningDeleteModal, setIsOpenWarningDeleteModal] =
    React.useState<boolean>(false);

  const { service: deactivateAccountService } = useServices(
    ServiceNameEnum.customerMgmt.deactivateAccount,
  );
  const { service: activateAccountService } = useServices(
    ServiceNameEnum.customerMgmt.activateAccount,
  );
  const { service: forgotPasswordService } = useServices(
    ServiceNameEnum.customerMgmt.forgotPassword,
  );
  const { service: deleteCustomerService } = useServices(
    ServiceNameEnum.customerMgmt.delete,
  );

  const handleSuccessfullyChange = (updateType: string): void => {
    let updatedContent = "";
    switch (updateType) {
      case CustomerEnum.customerUpdateType.deactivate:
      case CustomerEnum.customerUpdateType.activate:
        updatedContent = "Changes have been made successfully";
        break;
      case CustomerEnum.customerUpdateType.sendForgotPasswordLink:
        updatedContent = "Send forgot password link successfully";
        break;
      default:
        updatedContent = "Changes have been made successfully";
        break;
    }
    setSuccessDialogState({
      isOpen: true,
      content: updatedContent,
    });
    setTimeout(
      () => setSuccessDialogState({ ...successDialogState, isOpen: false }),
      3000,
    );
  };

  const handleDeactivateAccount = async (): Promise<void> => {
    if (customerId) {
      const { data, error } = await deactivateAccountService.post(
        {},
        { params: { id: customerId } },
      );

      if (data) {
        // show message
        handleSuccessfullyChange(CustomerEnum.customerUpdateType.deactivate);
        setIsOpenDeactivateModal(false);
        refetchData();
      } else if (error) {
        const errorResponse = AxiosService.getErrorResponse(error);
        toast.error(errorResponse?.message);
      }
    }
  };

  const handleDeleteConfirm = (): void => {
    if (isActive) {
      setIsOpenWarningDeleteModal(true);
    } else {
      setIsOpenDeleteCustomerModal(true);
    }
  };

  const handleDeactivateConfirm = (): void => {
    if (isActive) {
      setIsOpenDeactivateModal(true);
    }
  };

  const handleActivateConfirm = (): void => {
    setIsOpenActivateModal(true);
  };

  const handleDeleteCustomer = async (): Promise<void> => {
    const params = { id: customerId };
    const { data, error } = await deleteCustomerService.delete({ params });
    if (data) {
      toast.success("Successfully deleted account");
      navigate(RouteNameEnum.customer.index);
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message);
    }
  };

  // TODO: activate deactivated user ?????
  const handleActivateAccount = async (): Promise<void> => {
    if (customerId) {
      const { data, error } = await activateAccountService.post(
        {},
        { params: { id: customerId } },
      );

      if (data) {
        handleSuccessfullyChange(CustomerEnum.customerUpdateType.activate);
        setIsOpenActivateModal(false);
        refetchData();
      } else if (error) {
        const errorResponse = AxiosService.getErrorResponse(error);
        toast.error(errorResponse?.message);
      }
    }
  };

  const handleConfirmSendForgotPasswordLink = async (): Promise<void> => {
    const { data, error } = await forgotPasswordService.post({ email });
    if (data) {
      handleSuccessfullyChange(
        CustomerEnum.customerUpdateType.sendForgotPasswordLink,
      );
      refetchData();
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message);
    }
  };

  return (
    <div
      style={{
        padding: 24,
        borderRadius: "12px",
        boxShadow: "2px 2px 27px 0px rgba(0, 0, 0, 0.08)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ display: "flex" }}>
          <Avatar src={imageURL} style={{ width: 76, height: 76 }} />
          <div style={{ marginLeft: 24 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Typography.Text strong style={{ marginRight: 16, fontSize: 16 }}>
                {displayName}
              </Typography.Text>
              <StatusBadge status={isActive} />
            </div>
            <Typography.Text type="secondary">{email}</Typography.Text>
            <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
              <DashedBorderCard title="Date created" value={createdAt} />
              <DashedBorderCard title="Last updated" value={updatedAt} />
              <DashedBorderCard title="Last logged in" value={latestLoginAt} />
            </div>
          </div>
        </div>
        {checkPermission([
          PermissionEnum.code.customerSendForgotPassword,
          PermissionEnum.code.customerDeactivate,
          PermissionEnum.code.customerDelete,
        ]) && (
          <MoreOptionMenu
            isActive={isActive}
            isSso={isSso}
            handleConfirmSendForgotPasswordLink={
              handleConfirmSendForgotPasswordLink
            }
            handleDeactivateAccountConfirm={handleDeactivateConfirm}
            handleDeleteConfirm={handleDeleteConfirm}
            handleActivateAccountConfirm={handleActivateConfirm}
          />
        )}
        {customerId && (
          <ConfirmModal
            isOpen={isOpenDeactivateModal}
            icon={warningIcon}
            message="Are you sure you want to deactivate this account?"
            handleClose={() => setIsOpenDeactivateModal(false)}
            handleConfirm={() => handleDeactivateAccount()}
            labelCancel="Back"
            labelConfirm="Confirm"
          />
        )}
        {customerId && (
          <ConfirmModal
            isOpen={isOpenActivateModal}
            icon={warningIcon}
            message="Are you sure you want to active this customer account?"
            handleClose={() => setIsOpenActivateModal(false)}
            handleConfirm={() => handleActivateAccount()}
            labelCancel="Back"
            labelConfirm="Yes"
          />
        )}
        {customerId && (
          <ConfirmModal
            isOpen={isOpenDeleteCustomerModal}
            icon={deleteIcon}
            message="Are you sure you want to delete this customer account?"
            handleClose={() => setIsOpenDeleteCustomerModal(false)}
            handleConfirm={() => handleDeleteCustomer()}
            labelCancel="Cancel"
            labelConfirm="Delete"
          />
        )}
        <WarningDeleteCustomerModel
          isOpen={isOpenWarningDeleteModal}
          handleClose={() => setIsOpenWarningDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default CustomerDetailCard;
